/**
 * After a deploy, a page left open asks for code by the old build's file
 * names, and the server no longer has them: the dynamic import fails and
 * the route cannot render. The cure is one reload, onto the new build.
 * The reload is remembered in session storage for a minute, so a chunk
 * that is missing for some other reason shows the error instead of
 * reloading forever.
 */

export const CHUNK_RELOAD_KEY = "jobenvoy-chunk-reload-v1";
export const CHUNK_RELOAD_WINDOW_MS = 60_000;

/** Whether the error is a failed load of the app's own code. */
export function isChunkLoadError(error: unknown): boolean {
    if (!(error instanceof Error)) return false;
    return (
        error.name === "ChunkLoadError" ||
        /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/i.test(
            error.message,
        )
    );
}

/** Whether a reload may go now, given when the last one for a chunk went. */
export function mayReloadForChunk(last: number | null, now: number): boolean {
    return last === null || !(now - last >= 0 && now - last < CHUNK_RELOAD_WINDOW_MS);
}

function lastReload(storage: Storage | null): number | null {
    try {
        const value = Number(storage?.getItem(CHUNK_RELOAD_KEY));
        return Number.isFinite(value) && value > 0 ? value : null;
    } catch {
        return null;
    }
}

/** Reloads onto the new build when allowed, and says whether it did. */
export function reloadForChunk(
    reload: () => void,
    storage: Storage | null,
    now: number = Date.now(),
): boolean {
    if (!mayReloadForChunk(lastReload(storage), now)) return false;
    try {
        storage?.setItem(CHUNK_RELOAD_KEY, String(now));
    } catch {
        // Storage refused: reload once anyway.
    }
    reload();
    return true;
}
